/**
 * Employees Tab Component
 * Features: CSV/PDF export, configurable row count, contextual empty state
 */

import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import { Users } from "lucide-react";
import { 
  ChartCard, TableCard, CustomTooltip, DataTable, 
  TableRow, TableCell, EmptyState, EMPTY_STATES 
} from "./shared";
import {
  CATEGORY_COLORS, CHART_STYLE, formatCurrency, formatNumber, 
  exportToCSV, DEFAULT_ROW_COUNT,
  generateAnalyticsPdfContent, generateTableHtml,
} from "../../lib/chartUtils";
import useExportDialog from "../../hooks/useExportDialog";

const EmployeesTab = ({ data }) => {
  const [rowCount, setRowCount] = useState(DEFAULT_ROW_COUNT);
  const { exportToPdf } = useExportDialog();

  if (!data?.length) {
    return (
      <EmptyState 
        icon={Users}
        {...EMPTY_STATES.employees}
      />
    );
  }

  const handleExportCSV = () => {
    exportToCSV(data, "employee-performance",
      ["Name", "Position", "Hours", "Earnings", "Jobs", "Days", "Avg Hours/Day"],
      {
        "Name": "employee_name",
        "Position": "position",
        "Hours": "total_hours",
        "Earnings": "total_earnings",
        "Jobs": "jobs_worked",
        "Days": "days_worked",
        "Avg Hours/Day": "avg_hours_per_day",
      }
    );
  };

  const handleExportPDF = () => {
    const tableRows = data.slice(0, rowCount).map(emp => [
      emp.employee_name,
      emp.position || "-",
      `${formatNumber(emp.total_hours)}h`,
      formatCurrency(emp.total_earnings),
      emp.jobs_worked,
      emp.days_worked,
      `${emp.avg_hours_per_day}h`,
    ]);

    const tableHtml = generateTableHtml(
      ["Name", "Position", "Hours", "Earnings", "Jobs", "Days", "Avg Hrs/Day"],
      tableRows,
      ["left", "left", "right", "right", "right", "right", "right"]
    );

    const totals = data.reduce((acc, emp) => ({
      hours: acc.hours + emp.total_hours,
      earnings: acc.earnings + emp.total_earnings,
    }), { hours: 0, earnings: 0 });

    const summaryHtml = `
      <div style="display: flex; gap: 24px; margin-bottom: 16px;">
        <div><strong>Total Employees:</strong> ${data.length}</div>
        <div><strong>Total Hours:</strong> ${formatNumber(totals.hours)}h</div>
        <div><strong>Total Earnings:</strong> ${formatCurrency(totals.earnings)}</div>
      </div>
    `;

    const content = generateAnalyticsPdfContent("Employee Performance Report", [
      { title: "Summary", content: summaryHtml },
      { title: "Employee Details", content: tableHtml },
    ]);

    exportToPdf("Employee Performance Report", content);
  };

  return (
    <div className="space-y-6">
      {/* Top Performers Chart */}
      <ChartCard title="Employee Hours Ranking">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data.slice(0, 10)} layout="vertical" margin={{ top: 20, right: 40, left: 100, bottom: 20 }}>
            <CartesianGrid {...CHART_STYLE.grid} />
            <XAxis type="number" tickFormatter={(v) => `${v}h`} tick={CHART_STYLE.axis} />
            <YAxis dataKey="employee_name" type="category" tick={{ fontSize: 11 }} width={90} />
            <Tooltip content={<CustomTooltip formatter={(v) => `${v} hours`} />} />
            <Bar dataKey="total_hours" name="Total Hours" fill="#3b82f6" radius={[0, 4, 4, 0]}>
              {data.slice(0, 10).map((entry, index) => (
                <Cell key={entry.employee_id} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Employee Table */}
      <TableCard 
        title="Employee Performance Details" 
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        rowCount={rowCount}
        onRowCountChange={setRowCount}
      >
        <DataTable headers={[
          { label: "Employee" },
          { label: "Position" },
          { label: "Total Hours", align: "right" },
          { label: "Earnings", align: "right" },
          { label: "Jobs", align: "right" },
          { label: "Days Worked", align: "right" },
          { label: "Avg Hrs/Day", align: "right" },
        ]}>
          {data.slice(0, rowCount).map((emp) => (
            <TableRow key={emp.employee_id} className="table-row">
              <TableCell className="font-medium">{emp.employee_name}</TableCell>
              <TableCell>{emp.position || "-"}</TableCell>
              <TableCell className="text-right">{formatNumber(emp.total_hours)}h</TableCell>
              <TableCell className="text-right">{formatCurrency(emp.total_earnings)}</TableCell>
              <TableCell className="text-right">{emp.jobs_worked}</TableCell>
              <TableCell className="text-right">{emp.days_worked}</TableCell>
              <TableCell className="text-right">{emp.avg_hours_per_day}h</TableCell>
            </TableRow>
          ))}
        </DataTable>
      </TableCard>
    </div>
  );
};

export default EmployeesTab;
